import { useEffect, useMemo, useState } from "react";
import useEventCallback from "hooks/useEventCallback";

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;

type UseCountdownReturn = {
  /** `true` while there is time left on the clock. */
  isCountingDown: boolean;
  /** Whole seconds left, rounded up, so it only reaches `0` once the countdown is over. */
  secondsRemaining: number;
  /** Starts (or restarts) the countdown, defaulting to the duration the hook was created with. */
  start: (ms?: number) => void;
  /** Cancels the countdown immediately. */
  stop: () => void;
};

/**
 * @description Custom hook that counts down from a given duration, exposing the whole seconds left.
 */
function useCountdown(defaultMs = MS_PER_MINUTE): UseCountdownReturn {
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);

  useEffect(() => {
    if (endsAt === null) return undefined;

    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = () => {
      clearTimeout(timeoutId);

      const remaining = Math.max(0, endsAt - Date.now());
      setRemainingMs(remaining);

      if (remaining === 0) {
        setEndsAt(null);
        return;
      }

      timeoutId = setTimeout(tick, remaining % MS_PER_SECOND || MS_PER_SECOND);
    };

    tick();

    /**
     * Browsers throttle timers in background tabs, so re-sync as soon as the tab is visible again
     */
    document.addEventListener("visibilitychange", tick);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [endsAt]);

  const start = useEventCallback((ms: number = defaultMs) => {
    setEndsAt(Date.now() + Math.max(0, ms));
  });

  const stop = useEventCallback(() => {
    setEndsAt(null);
    setRemainingMs(0);
  });

  return useMemo(
    () => ({
      isCountingDown: remainingMs > 0,
      secondsRemaining: Math.ceil(remainingMs / MS_PER_SECOND),
      start,
      stop,
    }),
    [remainingMs, start, stop]
  );
}

export default useCountdown;
